/**
 * Racket Comparison Lab — Prime Padel Shop
 * assets/racket-lab.js
 *
 * Two parts:
 *   1. RacketParse  – turns a raw Shopify product (metafields, tags, title,
 *                     description) into clean comparison specs. Pure functions,
 *                     no DOM, so tools/build-racket-csv.js can reuse them in Node.
 *   2. RacketLab    – the comparison UI (slots, grid, filters, Ajax cart).
 *
 * Data priority for every spec (per field):
 *   racket.* metafield  →  product tags  →  product title  →  product description
 * The description is only read on demand (when a racket is loaded into a slot)
 * so the grid stays fast.
 *
 * No dependencies. Works with Online Store 2.0 themes; has extra hooks for
 * the Futurer theme (xo-* components) and Dawn-style cart drawers.
 */
(function (global) {
  'use strict';

  /* ======================================================================
   * 1. SPEC PARSING
   * ==================================================================== */

  var SHAPES = ['round', 'teardrop', 'diamond', 'hybrid'];
  var LEVELS = ['beginner', 'intermediate', 'advanced', 'pro'];
  var STYLES = ['control', 'versatile', 'power', 'attack'];

  var LABELS = {
    round: 'Round', teardrop: 'Teardrop', diamond: 'Diamond', hybrid: 'Hybrid',
    beginner: 'Beginner', intermediate: 'Intermediate', advanced: 'Advanced', pro: 'Pro',
    control: 'Control', versatile: 'Hybrid', power: 'Power', attack: 'Attack'
  };

  // Keyword → canonical value. Order inside each list does not matter;
  // a text can match several values (e.g. "Beginner-Intermediate").
  var SHAPE_RX = {
    round: /\bround\b/i,
    teardrop: /\btear[\s-]?drop\b|\bdrop\b/i,
    diamond: /\bdiamond\b/i,
    hybrid: /\bhybrid\b/i
  };
  var LEVEL_RX = {
    beginner: /\bbeginners?\b|\bjunior\b|\bkids?\b|\bstarter\b|\bimprover\b/i,
    intermediate: /\bintermediate\b|\bimprover\b/i,
    advanced: /\badvanced\b|\bintermediate\s*\+|\bexpert\b/i,
    pro: /\bpro\b|\bprofessional\b|\btournament\b/i
  };
  var STYLE_RX = {
    control: /\bcontrol\b|\bctrl\b|\bprecision\b/i,
    versatile: /\bversatile\b|\ball[\s-]?round(?:er)?\b|\bpolyvalent\b|\bbalanced\b/i,
    power: /\bpower\b/i,
    attack: /\battack\b|\boffensive\b|\baggressive\b/i
  };

  /** Return every canonical value whose keyword appears in `text`, in canonical order. */
  function matchAll(text, order, rxMap) {
    if (!text) return [];
    return order.filter(function (key) { return rxMap[key].test(text); });
  }

  /** Text after "Padel Racket" – where the store lists specs ("- Teardrop, Pro, Versatile"). */
  function titleSpecPart(title) {
    var m = /padel\s+(?:racket|racquet|raquet|raqueta|paddle)s?\b(.*)$/i.exec(title || '');
    return m ? m[1].replace(/\|.*$/, '') : '';   // drop "| Player Name"
  }

  /** Values from tags. Tags must match a keyword exactly (e.g. "teardrop", "advanced"). */
  function fromTags(tags, order, rxMap) {
    var list = (tags || []).map(function (t) { return String(t).toLowerCase().trim(); });
    return order.filter(function (key) {
      return list.some(function (t) { return t === key || (rxMap[key].test(t) && t.split(/\s+/).length <= 2); });
    });
  }

  /** "365-375g", "365–375 g", "370 ± 10 g", "approx. 360 g" … → "365–375 g" */
  function parseWeight(text) {
    if (!text) return '';
    var t = String(text).replace(/&nbsp;/g, ' ');
    var range = /(\d{3})\s*(?:g|gr|grams)?\s*(?:-|–|—|to)\s*(\d{3})\s*(?:g|gr|grams)\b/i.exec(t);
    if (range && inWeightRange(range[1]) && inWeightRange(range[2])) return range[1] + '–' + range[2] + ' g';
    var pm = /(\d{3})\s*(?:g|gr)?\s*(?:±|\+\/-|\+-)\s*(\d{1,2})\s*(?:g|gr|grams)?\b/i.exec(t);
    if (pm && inWeightRange(pm[1])) return pm[1] + ' ± ' + pm[2] + ' g';
    // Prefer a number that sits near the word "weight", then any plausible "360 g".
    var near = /weight[^0-9]{0,30}(\d{3})\s*(?:g|gr|grams)\b/i.exec(t);
    if (near && inWeightRange(near[1])) return near[1] + ' g';
    var re = /(?:^|[^\d])(\d{3})\s*(?:g|gr|grams)\b/gi, m;
    while ((m = re.exec(t))) { if (inWeightRange(m[1])) return m[1] + ' g'; }
    return '';
  }
  function inWeightRange(n) { n = +n; return n >= 300 && n <= 400; }

  /**
   * Balance → "Head-light" | "Even" | "Head-heavy" (with "Slightly" kept).
   * A bare measurement ("Balance: 26.5 cm") is mapped with common padel ranges:
   * < 25.5 cm head-light, 25.5–26.4 even, ≥ 26.5 head-heavy. The cm value is kept.
   */
  function parseBalance(text) {
    if (!text) return '';
    var t = String(text).replace(/weight\s*&\s*balance\s*system/ig, ' ');
    // 1) A labelled spec line wins ("Balance: Head Heavy", "Balance Balanced", "Balance: Slightly head-light").
    var lab = /balance\s*:?\s*(slightly\s+|very\s+)?(head[\s-]?heavy|head[\s-]?light|(?:mid|medium)[\s\/-]*(?:high|low)|high|low|medium|even|balanced|neutral|mid)\b/i.exec(t);
    if (lab) {
      var w = lab[2].toLowerCase(), slight = /slightly/i.test(lab[1] || '');
      // "Mid / High", "medium-high" → slightly head-heavy; "Mid / Low" → slightly head-light
      if (/^(mid|medium)[\s\/-]*(high|low)$/.test(w)) { slight = true; w = /high$/.test(w) ? 'high' : 'low'; }
      var base = /heavy|^high/.test(w) ? 'head-heavy' : /light|^low/.test(w) ? 'head-light' : 'even';
      if (base === 'even') return 'Even';
      return slight ? 'Slightly ' + base : base.charAt(0).toUpperCase() + base.slice(1);
    }
    var hh = /(slightly\s+)?head[\s-]?heavy|(slightly\s+)?high\s+balance|balance[:\s]+(?:slightly\s+)?high/i.exec(t);
    var hl = /(slightly\s+)?head[\s-]?light|(slightly\s+)?low\s+balance|balance[:\s]+(?:slightly\s+)?low/i.exec(t);
    var ev = /\b(?:even|medium|neutral|mid|balanced)[\s-]+balance|balance[:\s]+(?:even|medium|neutral|mid|balanced)/i.exec(t);
    // Only trust a keyword when the text doesn't also mention the opposite.
    if (hh && !hl) return /slightly/i.test(hh[0]) ? 'Slightly head-heavy' : 'Head-heavy';
    if (hl && !hh) return /slightly/i.test(hl[0]) ? 'Slightly head-light' : 'Head-light';
    if (ev) return 'Even';
    var cm = /balance[^0-9]{0,15}(\d{2}(?:[.,]\d)?)\s*(?:cm|mm)/i.exec(t);
    if (cm) {
      var v = parseFloat(cm[1].replace(',', '.'));
      if (v > 100) v = v / 10;                       // mm → cm
      if (v >= 23 && v <= 29) {
        var label = v < 25.5 ? 'Head-light' : v < 26.5 ? 'Even' : 'Head-heavy';
        return label + ' (' + v + ' cm)';
      }
    }
    return '';
  }

  /** "12K Alum Xtrem", "Carbon Aluminized 16K" → "12K carbon"; fiberglass faces → "Fiberglass". */
  function parseCarbon(text) {
    if (!text) return '';
    var k = /\b(\d{1,2})\s?K\b/.exec(String(text));
    if (k && +k[1] >= 1 && +k[1] <= 30) return k[1] + 'K carbon';
    if (/fib(?:er|re)\s*-?glass|glass\s*fib(?:er|re)/i.test(text)) return 'Fiberglass';
    return '';
  }

  /** "NOX AT10 Luxury Genius 12K 2026 Padel Racket - Teardrop, Pro" → "AT10 Luxury Genius 12K 2026" */
  function shortName(title, vendor) {
    var name = String(title || '');
    var v = String(vendor || '').trim();
    var firstWord = v.split(/\s+/)[0];
    [v, firstWord].forEach(function (prefix) {
      if (prefix && name.toLowerCase().indexOf(prefix.toLowerCase() + ' ') === 0) name = name.slice(prefix.length + 1);
    });
    name = name.replace(/\s*padel\s+(?:racket|racquet|raquet|raqueta|paddle)s?\b.*$/i, '')
      .replace(/\(\s*\)/g, '')
      .replace(/\s{2,}/g, ' ')
      .trim();
    return name || String(title || '');
  }

  function stripHtml(html) {
    return String(html || '')
      .replace(/<(br|\/p|\/li|\/div|\/h\d|\/tr)[^>]*>/gi, '\n')
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&#39;|&rsquo;/g, "'")
      .replace(/[ \t]+/g, ' ');
  }

  /** Split a metafield value like "beginner, intermediate" into canonical values. */
  function fromMetafield(value, order, rxMap) {
    return value ? matchAll(String(value), order, rxMap) : [];
  }

  /**
   * Build a clean racket record from the JSON the section renders
   * (or from /products.json in Node – see tools/build-racket-csv.js).
   * `src` records where each value came from: metafield | tag | title | description.
   */
  function buildRacket(raw) {
    var mf = raw.mf || {};
    var title = raw.title || '';
    var specPart = titleSpecPart(title);
    var src = {};

    function pick(field, order, rxMap, titleFallbackFull) {
      var v = fromMetafield(mf[field], order, rxMap);
      if (v.length) { src[field] = 'metafield'; return v; }
      v = fromTags(raw.tags, order, rxMap);
      if (v.length) { src[field] = 'tag'; return v; }
      v = matchAll(specPart, order, rxMap);
      // Play style may also live in the model name ("Metalbone CTRL", "Genius Attack").
      // Shape/level never do this safely ("Ventus Hybrid" is a teardrop, "Defy V1 Pro" is a model).
      if (!v.length && titleFallbackFull) v = matchAll(title.replace(/padel\s+racket.*$/i, ''), order, rxMap);
      if (v.length) { src[field] = 'title'; return v; }
      return [];
    }

    var shapes = pick('shape', SHAPES, SHAPE_RX, false);
    var levels = pick('level', LEVELS, LEVEL_RX, false);
    var styles = pick('play_style', STYLES, STYLE_RX, true);

    var weight = mf.weight ? String(mf.weight) : '';
    if (weight) src.weight = 'metafield';
    else if ((weight = parseWeight(title))) src.weight = 'title';

    var balance = mf.balance ? String(mf.balance) : '';
    if (balance) src.balance = 'metafield';

    if (mf.short_name) src.short_name = 'metafield';

    var carbon = mf.carbon ? String(mf.carbon) : '';
    if (carbon) src.carbon = 'metafield';
    else if ((carbon = parseCarbon(title))) src.carbon = 'title';

    /* Store groupings shown to shoppers (data in Shopify keeps the finer detail):
       shape  → Round · Teardrop (hybrid shapes count as teardrop) · Diamond
       level  → Beginner · Intermediate · Advanced (pro counts as advanced)
       style  → Control · Hybrid (= versatile) · Attack (power counts as attack) */
    var uniq = function (a) { return a.filter(function (v, i) { return a.indexOf(v) === i; }); };
    if (shapes[0] === 'hybrid') shapes = ['teardrop'];
    levels = uniq(levels.map(function (l) { return l === 'pro' ? 'advanced' : l; }));
    styles = uniq(styles.map(function (st) { return st === 'power' ? 'attack' : st; }));

    var r = {
      id: raw.id,
      handle: raw.handle,
      url: raw.url || ('/products/' + raw.handle),
      title: title,
      brand: raw.vendor || '',
      name: mf.short_name ? String(mf.short_name) : shortName(title, raw.vendor),
      shape: shapes[0] || '',          // one shape per racket
      levels: levels,                  // can span several ("Beginner–Intermediate")
      styles: styles,                  // can be several ("Power / Attack")
      weight: weight,
      balance: balance,
      carbon: carbon,
      img: raw.img || '',
      alt: raw.alt || title,
      available: raw.available !== false,
      price: raw.price || 0,
      priceF: raw.priceF || '',
      cmp: raw.cmp || 0,
      cmpF: raw.cmpF || '',
      variants: raw.variants || [],
      index: raw.index || 0,
      src: src,
      descLoaded: false
    };
    if (raw.description) applyDescription(r, raw.description);
    return r;
  }

  /** Fill gaps (only gaps) from the product description. */
  function applyDescription(r, html) {
    var text = stripHtml(html);
    if (!r.shape) {
      var s = /shape[:\s]+(round|tear[\s-]?drop|diamond|hybrid)/i.exec(text);
      if (s) { r.shape = matchAll(s[1], SHAPES, SHAPE_RX)[0] || ''; if (r.shape === 'hybrid') r.shape = 'teardrop'; if (r.shape) r.src.shape = 'description'; }
    }
    if (!r.weight && (r.weight = parseWeight(text))) r.src.weight = 'description';
    if (!r.balance && (r.balance = parseBalance(text))) r.src.balance = 'description';
    if (!r.carbon && (r.carbon = parseCarbon(text))) r.src.carbon = 'description';
    r.descLoaded = true;
    return r;
  }

  /** ["beginner","intermediate"] → "Beginner–Intermediate"; styles joined with " / ". */
  function formatLevels(levels) {
    if (!levels || !levels.length) return '';
    if (levels.length === 1) return LABELS[levels[0]];
    return LABELS[levels[0]] + '–' + LABELS[levels[levels.length - 1]];
  }
  function formatStyles(styles) {
    return (styles || []).map(function (s) { return LABELS[s]; }).join(' / ');
  }

  var RacketParse = {
    SHAPES: SHAPES, LEVELS: LEVELS, STYLES: STYLES, LABELS: LABELS,
    buildRacket: buildRacket, applyDescription: applyDescription,
    parseWeight: parseWeight, parseBalance: parseBalance, parseCarbon: parseCarbon,
    shortName: shortName, titleSpecPart: titleSpecPart, stripHtml: stripHtml,
    formatLevels: formatLevels, formatStyles: formatStyles
  };

  // Node (CSV tool) stops here.
  if (typeof module === 'object' && module.exports) { module.exports = RacketParse; return; }
  global.RacketLabParse = RacketParse;

  /* ======================================================================
   * 2. UI
   * ==================================================================== */

  var MAX_SLOTS = 4;
  var SPEC_ROWS = [
    { key: 'shape', label: 'Shape', get: function (r) { return r.shape ? LABELS[r.shape] : ''; } },
    { key: 'level', label: 'Level', get: function (r) { return formatLevels(r.levels); } },
    { key: 'style', label: 'Play style', get: function (r) { return formatStyles(r.styles); } },
    { key: 'weight', label: 'Weight', get: function (r) { return r.weight; } },
    { key: 'balance', label: 'Balance', get: function (r) { return r.balance; } },
    { key: 'carbon', label: 'Face', get: function (r) { return r.carbon; } }
  ];

  var reduceMotion = global.matchMedia && global.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function norm(s) {
    return String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  }
  /** Shopify CDN image at a given width (keeps ?v= cache key). */
  function imgAt(url, w) {
    if (!url) return '';
    try { var u = new URL(url, location.origin); u.searchParams.set('width', w); return u.toString(); }
    catch (e) { return url; }
  }
  function srcset(url, widths) {
    return widths.map(function (w) { return imgAt(url, w) + ' ' + w + 'w'; }).join(', ');
  }
  function root() {
    return (global.Shopify && global.Shopify.routes && global.Shopify.routes.root) || '/';
  }

  function RacketLab(el) {
    this.el = el;
    this.sectionId = el.getAttribute('data-section-id');
    this.cfg = {
      initialTiles: parseInt(el.getAttribute('data-initial-tiles'), 10) || 36,
      parseDescription: el.getAttribute('data-parse-description') === 'true',
      preload: el.getAttribute('data-preload') === 'true',
      defaultRackets: (el.getAttribute('data-default-rackets') || '').split(',').map(function (s) { return s.trim(); }).filter(Boolean),
      brandOrder: (el.getAttribute('data-brand-order') || '').split(',').map(function (s) { return s.trim(); }).filter(Boolean),
      totalPages: parseInt(el.getAttribute('data-total-pages'), 10) || 1,
      // Vendors that are not racket brands (e.g. the shop's own name). Their brand is read from the title.
      ignoreVendors: (el.getAttribute('data-ignore-vendors') || '').split(',').map(function (s) { return norm(s.trim()); }).filter(Boolean)
    };
    this.items = [];
    this.byHandle = {};
    this.slots = [null, null, null, null];     // handles
    this.visible = 3;
    this.variantSel = {};                      // handle → variant id
    this.onlyDiff = false;                     // "Only show differences" toggle
    this.cartState = {};                       // handle → { status: 'loading' | 'added' }
    this.cartMsg = {};                         // handle → last cart error
    this.filters = { brand: 'all', shape: 'all', level: 'all', style: 'all', sort: 'featured', q: '' };
    this.isPhone = !!(global.matchMedia && global.matchMedia('(max-width: 749px)').matches);
    if (this.isPhone) this.cfg.initialTiles = Math.min(this.cfg.initialTiles, 24);
    this.limit = this.cfg.initialTiles;

    this.$ = function (sel) { return el.querySelector(sel); };
    this.slotsEl = this.$('[data-rcl-slots]');
    this.gridEl = this.$('[data-rcl-grid]');
    this.live = this.$('[data-rcl-live]');

    this.loadInitialData();
    this.restoreFromUrl();
    this.applyDefaults();
    this.bind();
    this.renderBrandPills();
    this.renderSlots();
    this.renderGrid();
    this.setupTray();
    this.el.classList.add('is-ready');
    this.loadRemainingPages();
    this.slots.forEach(function (h) { if (h) this.ensureSpecs(h); }, this);
  }

  /* ---------- data ---------- */

  RacketLab.prototype.ingest = function (list) {
    var self = this, ignore = this.cfg.ignoreVendors;
    (list || []).forEach(function (raw) {
      if (!raw || self.byHandle[raw.handle]) return;
      raw.index = self.items.length;
      var r = buildRacket(raw);
      self.items.push(r);
      self.byHandle[r.handle] = r;
    });
    // Fix rackets whose vendor is not a brand: use the real brand that starts the title.
    var brands = this.items.map(function (r) { return r.brand; })
      .filter(function (b, i, a) { return b && a.indexOf(b) === i && ignore.indexOf(norm(b)) === -1; });
    this.items.forEach(function (r) {
      if (ignore.indexOf(norm(r.brand)) === -1) return;
      var match = brands.filter(function (b) { return norm(r.title).indexOf(norm(b) + ' ') === 0; })[0];
      if (match) { r.brand = match; r.name = r.src.short_name ? r.name : shortName(r.title, match); }
    });
  };

  RacketLab.prototype.loadInitialData = function () {
    var node = this.$('[data-rcl-data]');
    try { this.ingest(JSON.parse(node.textContent)); }
    catch (e) { console.error('[Racket Lab] Could not read product data', e); }
  };

  /** Collections larger than one Liquid page (250) are fetched via the Section Rendering API. */
  RacketLab.prototype.loadRemainingPages = function () {
    var self = this;
    if (self.cfg.totalPages < 2) return;
    var pages = [];
    for (var p = 2; p <= self.cfg.totalPages; p++) pages.push(p);
    pages.reduce(function (chain, page) {
      return chain.then(function () {
        var url = location.pathname + '?section_id=' + encodeURIComponent(self.sectionId) + '&page=' + page;
        return fetch(url).then(function (r) { return r.text(); }).then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var node = doc.querySelector('[data-rcl-data]');
          if (node) self.ingest(JSON.parse(node.textContent));
        });
      });
    }, Promise.resolve()).then(function () {
      self.renderBrandPills();
      self.renderGrid();
      self.restoreFromUrl(true);
    }).catch(function (e) { console.warn('[Racket Lab] Extra pages failed', e); });
  };

  /** Pull weight / balance / carbon / shape from the description when the fields are missing. */
  RacketLab.prototype.ensureSpecs = function (handle) {
    var self = this, r = this.byHandle[handle];
    if (!r || r.descLoaded || !this.cfg.parseDescription) return;
    if (r.shape && r.weight && r.balance && r.carbon) { r.descLoaded = true; return; }
    r.descLoaded = true;   // prevent duplicate requests
    fetch(root() + 'products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (p) {
        if (!p) return;
        applyDescription(r, p.description || '');
        if (self.slots.indexOf(handle) > -1) self.renderSlots();
      })
      .catch(function () { /* specs simply stay hidden */ });
  };

  /* ---------- URL state (?compare=a,b,,c&slots=4) ---------- */

  RacketLab.prototype.restoreFromUrl = function (onlyMissing) {
    var params = new URLSearchParams(location.search);
    var list = (params.get('compare') || '').split(',');
    if (!onlyMissing && params.get('slots') === '4') this.visible = 4;
    var changed = false;
    for (var i = 0; i < MAX_SLOTS; i++) {
      var h = list[i] && decodeURIComponent(list[i].trim());
      if (h && this.byHandle[h] && this.slots[i] !== h && this.slots.indexOf(h) === -1) {
        this.slots[i] = h; changed = true;
        if (i >= 3) this.visible = 4;
      }
    }
    if (onlyMissing && changed) { this.renderSlots(); this.updateTiles(); }
  };

  /**
   * First visit (no ?compare= in the URL): preload rackets so the page shows what it does,
   * like Jolie's page. Uses the merchant's picks, otherwise the first in-stock racket of
   * three different brands in collection order.
   */
  RacketLab.prototype.applyDefaults = function () {
    if (!this.cfg.preload || new URLSearchParams(location.search).has('compare')) return;
    if (this.slots.some(Boolean)) return;
    var self = this, picks = [];
    this.cfg.defaultRackets.forEach(function (h) { if (self.byHandle[h] && picks.indexOf(h) === -1) picks.push(h); });
    if (!picks.length) {
      var brands = [];
      this.items.forEach(function (r) {
        if (picks.length < 3 && r.available && brands.indexOf(r.brand) === -1) { picks.push(r.handle); brands.push(r.brand); }
      });
    }
    picks.slice(0, MAX_SLOTS).forEach(function (h, i) { self.slots[i] = h; if (i >= 3) self.visible = 4; });
  };

  RacketLab.prototype.syncUrl = function () {
    if (!global.history || !history.replaceState) return;
    var params = new URLSearchParams(location.search);
    var picks = this.slots.slice(0, this.visible);
    if (picks.some(Boolean)) params.set('compare', picks.map(function (h) { return h || ''; }).join(',').replace(/,+$/, ''));
    else params.delete('compare');
    if (this.visible === 4) params.set('slots', '4'); else params.delete('slots');
    var qs = params.toString();
    history.replaceState(history.state, '', location.pathname + (qs ? '?' + qs : '') + location.hash);
  };

  /* ---------- events ---------- */

  RacketLab.prototype.bind = function () {
    var self = this, el = this.el;

    el.addEventListener('click', function (e) {
      var t = e.target.closest('[data-action]');
      if (!t || !el.contains(t)) return;
      var action = t.getAttribute('data-action');
      var slot = parseInt(t.getAttribute('data-slot'), 10);
      switch (action) {
        case 'pick': self.pick(t.getAttribute('data-handle')); break;
        case 'remove': self.removeSlot(slot); break;
        case 'move': self.moveSlot(slot, slot + parseInt(t.getAttribute('data-dir'), 10), true); break;
        case 'add-cart': self.addToCart(slot); break;
        case 'toggle-fourth': self.toggleFourth(); break;
        case 'clear-all': self.clearAll(); break;
        case 'brand': self.setFilter('brand', t.getAttribute('data-value')); break;
        case 'more': self.limit += self.cfg.initialTiles; self.renderGrid(true); break;
        case 'reset-filters': self.resetFilters(); break;
        case 'scroll-to-slots': self.scrollToSlots(); break;
        case 'open-filters': self.openSheet(); break;
        case 'close-filters': self.closeSheet(); break;
      }
    });

    el.addEventListener('change', function (e) {
      var t = e.target;
      if (t.matches('[data-filter]')) self.setFilter(t.getAttribute('data-filter'), t.value);
      if (t.matches('[data-rcl-only-diff]')) {
        self.onlyDiff = t.checked;
        self.renderSlots();
        self.announce(t.checked ? 'Highlighting the specs that differ.' : 'Differences highlight off.');
      }
      if (t.matches('[data-variant]')) {
        var h = t.getAttribute('data-variant');
        self.variantSel[h] = parseInt(t.value, 10);
        self.renderSlots();
      }
    });

    var search = this.$('[data-rcl-search]'), timer;
    if (search) search.addEventListener('input', function () {
      clearTimeout(timer);
      timer = setTimeout(function () { self.setFilter('q', search.value); }, 140);
    });

    this.bindDrag();

    // Mobile filter sheet: Escape closes it
    el.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && el.classList.contains('is-sheet-open')) self.closeSheet();
    });
    // Mobile swipe dots follow the slot scroller
    var raf;
    this.slotsEl.addEventListener('scroll', function () {
      cancelAnimationFrame(raf); raf = requestAnimationFrame(function () { self.updateDots(); });
    }, { passive: true });
  };

  /* ---------- mobile filter sheet ---------- */

  RacketLab.prototype.openSheet = function () {
    var sheet = this.$('[data-rcl-sheet]'), toggle = this.$('[data-action="open-filters"]'), bd = this.$('.rcl-sheet-backdrop');
    if (!sheet) return;
    if (bd) bd.hidden = false;
    this.el.classList.add('is-sheet-open');
    if (toggle) toggle.setAttribute('aria-expanded', 'true');
    sheet.setAttribute('role', 'dialog'); sheet.setAttribute('aria-modal', 'true');
    document.documentElement.style.overflow = 'hidden';          // no page scroll behind the sheet
    var first = sheet.querySelector('select');
    setTimeout(function () { if (first) first.focus({ preventScroll: true }); }, 60);
  };

  RacketLab.prototype.closeSheet = function () {
    var sheet = this.$('[data-rcl-sheet]'), toggle = this.$('[data-action="open-filters"]'), bd = this.$('.rcl-sheet-backdrop');
    if (!this.el.classList.contains('is-sheet-open')) return;
    this.el.classList.remove('is-sheet-open');
    if (toggle) { toggle.setAttribute('aria-expanded', 'false'); toggle.focus({ preventScroll: true }); }
    if (sheet) { sheet.removeAttribute('role'); sheet.removeAttribute('aria-modal'); }
    document.documentElement.style.overflow = '';
    setTimeout(function () { if (bd) bd.hidden = true; }, 250);
    var grid = this.gridEl;
    if (this.isPhone && grid) grid.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  };

  /* ---------- mobile swipe dots under the slots ---------- */

  RacketLab.prototype.updateDots = function () {
    var box = this.$('[data-rcl-dots]');
    if (!box) return;
    var n = this.visible, sl = this.slotsEl;
    if (box.children.length !== n) box.innerHTML = new Array(n + 1).join('<span></span>');
    var slot = sl.querySelector('.rcl-slot');
    var w = slot ? slot.offsetWidth + 10 : 1;
    var first = Math.round(sl.scrollLeft / w);
    var perView = Math.max(1, Math.round(sl.clientWidth / w));
    // hide the dots when every slot already fits on screen
    box.style.visibility = n > perView ? 'visible' : 'hidden';
    [].forEach.call(box.children, function (d, i) { d.classList.toggle('is-on', i >= first && i < first + perView); });
  };

  /** Desktop drag & drop: drag a slot by its header, drop on another slot. */
  RacketLab.prototype.bindDrag = function () {
    var self = this, from = null, box = this.slotsEl;

    box.addEventListener('dragstart', function (e) {
      var head = e.target.closest && e.target.closest('[data-drag-handle]');
      if (!head) return;
      from = parseInt(head.getAttribute('data-slot'), 10);
      e.dataTransfer.effectAllowed = 'move';
      try { e.dataTransfer.setData('text/plain', String(from)); } catch (err) {}
      var slotEl = head.closest('.rcl-slot');
      if (slotEl && e.dataTransfer.setDragImage) e.dataTransfer.setDragImage(slotEl, 40, 20);
      requestAnimationFrame(function () { slotEl && slotEl.classList.add('is-dragging'); });
    });
    box.addEventListener('dragover', function (e) {
      if (from === null) return;
      var target = e.target.closest('.rcl-slot');
      if (!target) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      box.querySelectorAll('.is-drop-target').forEach(function (n) { if (n !== target) n.classList.remove('is-drop-target'); });
      if (parseInt(target.getAttribute('data-slot'), 10) !== from) target.classList.add('is-drop-target');
    });
    box.addEventListener('dragleave', function (e) {
      var target = e.target.closest('.rcl-slot');
      if (target && !target.contains(e.relatedTarget)) target.classList.remove('is-drop-target');
    });
    box.addEventListener('drop', function (e) {
      var target = e.target.closest('.rcl-slot');
      if (from === null || !target) return;
      e.preventDefault();
      self.moveSlot(from, parseInt(target.getAttribute('data-slot'), 10), false);
      from = null;
    });
    box.addEventListener('dragend', function () {
      from = null;
      box.querySelectorAll('.is-dragging, .is-drop-target').forEach(function (n) { n.classList.remove('is-dragging', 'is-drop-target'); });
    });
  };

  /* ---------- slot actions ---------- */

  /** Tile tap: first empty visible slot, otherwise replace the last one. */
  RacketLab.prototype.pick = function (handle) {
    var r = this.byHandle[handle];
    if (!r) return;
    var existing = this.slots.indexOf(handle);
    if (existing > -1 && existing < this.visible) {
      this.flash(existing);
      this.announce(r.name + ' is already in slot ' + (existing + 1) + '.');
      return;
    }
    var idx = this.slots.slice(0, this.visible).indexOf(null);
    var replaced = null;
    if (idx === -1) { idx = this.visible - 1; replaced = this.byHandle[this.slots[idx]]; }
    this.slots[idx] = handle;
    this.afterSlotsChange();
    this.flash(idx);
    this.announce(r.name + ' loaded into slot ' + (idx + 1) + (replaced ? ', replacing ' + replaced.name : '') + '.');
    this.ensureSpecs(handle);
    this.trayFeedback('Added to slot ' + (idx + 1));
    if (this.isPhone && navigator.vibrate) { try { navigator.vibrate(12); } catch (e) {} }
  };

  RacketLab.prototype.removeSlot = function (i) {
    var r = this.byHandle[this.slots[i]];
    this.slots[i] = null;
    this.afterSlotsChange();
    this.announce((r ? r.name + ' removed from slot ' : 'Cleared slot ') + (i + 1) + '.');
    this.focusSlot(i, '.rcl-slot__head');
  };

  /** Move slot `from` to position `to` (other slots shift). Used by drag and by arrow buttons. */
  RacketLab.prototype.moveSlot = function (from, to, keepFocus) {
    if (isNaN(from) || isNaN(to) || to < 0 || to >= this.visible || from === to) return;
    var item = this.slots.splice(from, 1)[0];
    this.slots.splice(to, 0, item);
    this.afterSlotsChange();
    this.flash(to);
    var r = this.byHandle[item];
    this.announce((r ? r.name : 'Empty slot') + ' moved to position ' + (to + 1) + '.');
    if (keepFocus) {
      // keep focus on the same arrow so repeated presses keep moving it
      var dir = to > from ? '1' : '-1';
      var btn = this.slotsEl.querySelector('.rcl-slot[data-slot="' + to + '"] [data-action="move"][data-dir="' + dir + '"]');
      if (btn && !btn.disabled) btn.focus(); else this.focusSlot(to, '[data-action="move"]:not([disabled])');
    }
  };

  RacketLab.prototype.toggleFourth = function () {
    if (this.visible === 3) {
      this.visible = 4;
      this.afterSlotsChange();
      this.announce('Fourth slot added.');
      this.focusSlot(3, '.rcl-slot__head');
      this.scrollSlotIntoView(3);
    } else {
      this.slots[3] = null;
      this.visible = 3;
      this.afterSlotsChange();
      this.announce('Back to three slots.');
      var btn = this.$('[data-action="toggle-fourth"]'); if (btn) btn.focus();
    }
  };

  /** "Reset comparison": empty every slot and go back to three slots. Filters are left alone. */
  RacketLab.prototype.clearAll = function () {
    this.slots = [null, null, null, null];
    this.visible = 3;
    this.cartMsg = {};
    this.afterSlotsChange();
    this.announce('Comparison reset. All slots are empty.');
    this.focusSlot(0, '.rcl-slot__head');
  };

  RacketLab.prototype.afterSlotsChange = function () {
    this.renderSlots();
    this.updateTiles();
    this.updateTray();
    this.syncUrl();
  };

  RacketLab.prototype.flash = function (i) {
    var node = this.slotsEl.querySelector('.rcl-slot[data-slot="' + i + '"]');
    if (!node) return;
    node.classList.remove('is-flash');
    void node.offsetWidth;                       // restart the animation
    node.classList.add('is-flash');
    setTimeout(function () { node.classList.remove('is-flash'); }, 1400);
    this.scrollSlotIntoView(i);
  };

  /** On the mobile snap-scroller, bring the slot into view horizontally (never jumps the page). */
  RacketLab.prototype.scrollSlotIntoView = function (i) {
    var box = this.slotsEl, node = box.querySelector('.rcl-slot[data-slot="' + i + '"]');
    if (!node || box.scrollWidth <= box.clientWidth) return;
    var left = node.offsetLeft - (box.clientWidth - node.offsetWidth) / 2;
    box.scrollTo({ left: Math.max(0, left), behavior: reduceMotion ? 'auto' : 'smooth' });
  };

  RacketLab.prototype.focusSlot = function (i, sel) {
    var node = this.slotsEl.querySelector('.rcl-slot[data-slot="' + i + '"] ' + sel);
    if (node) { if (!node.hasAttribute('tabindex') && !/^(BUTTON|A|SELECT|INPUT)$/.test(node.tagName)) node.setAttribute('tabindex', '-1'); node.focus({ preventScroll: true }); }
  };

  RacketLab.prototype.scrollToSlots = function () {
    var top = this.$('[data-rcl-compare]');
    if (top) top.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    var first = this.slotsEl.querySelector('.rcl-slot__head');
    if (first) { first.setAttribute('tabindex', '-1'); first.focus({ preventScroll: true }); }
  };

  RacketLab.prototype.announce = function (msg) {
    if (!this.live) return;
    this.live.textContent = '';
    var live = this.live;
    setTimeout(function () { live.textContent = msg; }, 30);
  };

  /* ---------- slot rendering ---------- */

  RacketLab.prototype.currentVariant = function (r) {
    var list = r.variants || [];
    var id = this.variantSel[r.handle];
    var v = list.filter(function (x) { return x.id === id; })[0];
    return v || list.filter(function (x) { return x.a; })[0] || list[0] || null;
  };

  RacketLab.prototype.renderSlots = function () {
    var self = this;
    var filled = this.slots.slice(0, this.visible).map(function (h) { return h && self.byHandle[h]; });

    // A spec row is shown when at least one loaded racket has a value;
    // rackets without that value show "—" so the rows stay aligned.
    var loaded = filled.filter(Boolean);
    var rows = SPEC_ROWS.filter(function (row) { return loaded.some(function (r) { return row.get(r); }); })
      .map(function (row) {
        // A row "differs" when 2+ rackets are loaded and their values aren't all the same.
        var vals = loaded.map(function (r) { return row.get(r) || '—'; });
        var diff = loaded.length > 1 && vals.some(function (v) { return v !== vals[0]; });
        return { key: row.key, label: row.label, get: row.get, diff: diff };
      });
    // "Differences" switch: keep every row, but spotlight the ones that differ (styling via .is-diff-mode)
    this.slotsEl.classList.toggle('is-diff-mode', !!this.onlyDiff && loaded.length > 1);
    var diffToggle = this.$('[data-rcl-only-diff]');
    if (diffToggle) diffToggle.disabled = loaded.length < 2;
    this.slotsEl.style.setProperty('--rcl-rows', String(5 + rows.length));
    this.slotsEl.style.setProperty('--rcl-cols', String(this.visible));
    this.slotsEl.setAttribute('data-count', String(this.visible));

    var html = '';
    for (var i = 0; i < this.visible; i++) html += this.slotHtml(i, filled[i], rows);
    this.slotsEl.innerHTML = html;

    // toolbar
    var n = filled.filter(Boolean).length;
    var status = this.$('[data-rcl-status]');
    if (status) status.textContent = n + ' of ' + this.visible + ' picked';
    var clear = this.$('[data-action="clear-all"]');
    if (clear) clear.disabled = n === 0 && this.visible === 3;
    this.updateDots();
    this.updateBrandPicks();
    var fourth = this.$('[data-action="toggle-fourth"]');
    if (fourth) {
      fourth.innerHTML = this.visible === 3
        ? '<span class="rcl-hide-sm">+ Compare a fourth racket</span><span class="rcl-show-sm">+ 4th racket</span>'
        : '<span class="rcl-hide-sm">− Back to 3 slots</span><span class="rcl-show-sm">− 3 slots</span>';
      fourth.setAttribute('aria-expanded', String(this.visible === 4));
    }
  };

  RacketLab.prototype.slotHtml = function (i, r, rows) {
    var n = i + 1, last = this.visible - 1;
    var label = r ? r.brand + ' ' + r.name : 'empty';
    var head =
      '<header class="rcl-slot__head" draggable="true" data-drag-handle data-slot="' + i + '" title="Drag to reorder">' +
        '<span class="rcl-slot__grip" aria-hidden="true"><svg width="10" height="16" viewBox="0 0 10 16"><g fill="currentColor"><circle cx="2" cy="2" r="1.5"/><circle cx="8" cy="2" r="1.5"/><circle cx="2" cy="8" r="1.5"/><circle cx="8" cy="8" r="1.5"/><circle cx="2" cy="14" r="1.5"/><circle cx="8" cy="14" r="1.5"/></g></svg></span>' +
        '<span class="rcl-slot__num"><span class="rcl-hide-sm">Racket </span>' + n + '</span>' +
        '<span class="rcl-slot__tools">' +
          '<button type="button" class="rcl-icon" data-action="move" data-dir="-1" data-slot="' + i + '"' + (i === 0 ? ' disabled' : '') + ' aria-label="Move slot ' + n + ' (' + esc(label) + ') left">' +
            '<svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true"><path d="M9 2 4 7l5 5" fill="none" stroke="currentColor" stroke-width="1.8"/></svg></button>' +
          '<button type="button" class="rcl-icon" data-action="move" data-dir="1" data-slot="' + i + '"' + (i === last ? ' disabled' : '') + ' aria-label="Move slot ' + n + ' (' + esc(label) + ') right">' +
            '<svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true"><path d="m5 2 5 5-5 5" fill="none" stroke="currentColor" stroke-width="1.8"/></svg></button>' +
          (r ? '<button type="button" class="rcl-icon rcl-icon--x" data-action="remove" data-slot="' + i + '" aria-label="Remove ' + esc(label) + ' from slot ' + n + '">' +
            '<svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true"><path d="m3 3 8 8M11 3l-8 8" fill="none" stroke="currentColor" stroke-width="1.8"/></svg></button>' : '') +
        '</span>' +
      '</header>';

    if (!r) {
      return '<article class="rcl-slot is-empty" data-slot="' + i + '" aria-label="Slot ' + n + ', empty">' + head +
        '<div class="rcl-slot__empty"><span class="rcl-slot__plus" aria-hidden="true">+</span><p>Pick a racket below</p></div></article>';
    }

    var v = this.currentVariant(r);
    var available = v ? !!v.a : r.available;
    var priceF = v ? v.pf : r.priceF;
    var cmpF = v ? (v.c > v.p ? v.cf : '') : (r.cmp > r.price ? r.cmpF : '');

    var loaded = this.slots.slice(0, this.visible).filter(Boolean).length;
    var specs = rows.map(function (row) {
      var val = row.get(r);
      var state = r && row.diff !== undefined && loaded > 1 ? (row.diff ? ' is-diff' : ' is-same') : '';
      return '<dl class="rcl-spec' + (val ? '' : ' is-missing') + state + '" data-row="' + row.key + '"><dt>' + row.label + '</dt><dd>' + (val ? esc(val) : '<span aria-label="Not listed">—</span>') + '</dd></dl>';
    }).join('');

    var variantSelect = '';
    if (r.variants.length > 1) {
      variantSelect = '<label class="rcl-visually-hidden" for="rcl-v-' + this.sectionId + '-' + i + '">Option for ' + esc(r.name) + '</label>' +
        '<select class="rcl-select rcl-select--sm" id="rcl-v-' + this.sectionId + '-' + i + '" data-variant="' + esc(r.handle) + '">' +
        r.variants.map(function (x) {
          return '<option value="' + x.id + '"' + (v && v.id === x.id ? ' selected' : '') + '>' + esc(x.t) + (x.a ? '' : ' — Sold out') + '</option>';
        }).join('') + '</select>';
    }

    return '<article class="rcl-slot" data-slot="' + i + '" aria-label="Slot ' + n + ': ' + esc(label) + '">' + head +
      '<a class="rcl-slot__media" href="' + esc(r.url) + '" tabindex="-1" aria-hidden="true">' +
        (r.img ? '<img src="' + imgAt(r.img, 500) + '" srcset="' + srcset(r.img, [300, 400, 500, 700]) + '" sizes="(min-width: 990px) 300px, 45vw" alt="" width="500" height="500" decoding="async">' : '<span class="rcl-noimg">' + esc(r.brand.charAt(0)) + '</span>') +
        (available ? '' : '<span class="rcl-badge rcl-badge--soldout">Sold out</span>') +
      '</a>' +
      '<div class="rcl-slot__title"><p class="rcl-brand">' + esc(r.brand) + '</p><h3 class="rcl-name" title="' + esc(r.title) + '">' + esc(r.name) + '</h3></div>' +
      '<p class="rcl-slot__price">' + (cmpF ? '<span class="rcl-price rcl-price--sale">' + priceF + '</span> <s class="rcl-price--was"><span class="rcl-visually-hidden">Regular price </span>' + cmpF + '</s>' : '<span class="rcl-price">' + priceF + '</span>') + '</p>' +
      specs +
      '<div class="rcl-slot__actions">' + variantSelect +
        this.cartButtonHtml(i, r, available, label) +
        '<a class="rcl-btn rcl-btn--ghost" href="' + esc(r.url) + '">View product<span class="rcl-visually-hidden">: ' + esc(label) + '</span></a>' +
        '<p class="rcl-slot__msg" data-rcl-msg role="status">' + esc(this.cartMsg[r.handle] || '') + '</p>' +
      '</div>' +
    '</article>';
  };

  /** Add-to-cart button; its busy/added state lives in this.cartState so re-renders keep it. */
  RacketLab.prototype.cartButtonHtml = function (i, r, available, label) {
    var st = (this.cartState[r.handle] || {}).status;
    var cls = st === 'loading' ? ' is-loading' : st === 'added' ? ' is-added' : '';
    var text = !available ? 'Sold out' : st === 'loading' ? 'Adding…' : st === 'added' ? 'Added to cart' : 'Add to cart';
    var aria = available ? 'Add ' + esc(label) + ' to cart' : esc(label) + ' is sold out';
    return '<button type="button" class="rcl-btn rcl-btn--primary' + cls + '" data-action="add-cart" data-slot="' + i + '"' +
      (!available || st ? ' disabled' : '') + ' aria-label="' + aria + '">' + text + '</button>';
  };

  /* ---------- filters & grid ---------- */

  RacketLab.prototype.setFilter = function (key, value) {
    this.filters[key] = value;
    this.limit = this.cfg.initialTiles;
    if (key === 'brand') {
      this.el.querySelectorAll('[data-action="brand"]').forEach(function (b) {
        b.setAttribute('aria-pressed', String(b.getAttribute('data-value') === value));
      });
    }
    this.renderGrid();     // slots are never touched here
  };

  RacketLab.prototype.resetFilters = function () {
    this.filters = { brand: 'all', shape: 'all', level: 'all', style: 'all', sort: this.filters.sort, q: '' };
    this.el.querySelectorAll('[data-filter]').forEach(function (s) { if (s.getAttribute('data-filter') !== 'sort') s.value = 'all'; });
    var search = this.$('[data-rcl-search]'); if (search) search.value = '';
    this.setFilter('brand', 'all');
  };

  RacketLab.prototype.renderBrandPills = function () {
    var box = this.$('[data-rcl-brands]');
    if (!box) return;
    var counts = {}, total = this.items.length;
    this.items.forEach(function (r) { if (r.brand) counts[r.brand] = (counts[r.brand] || 0) + 1; });
    var order = this.cfg.brandOrder.map(norm);
    var brands = Object.keys(counts).sort(function (a, b) {
      var ia = order.indexOf(norm(a)), ib = order.indexOf(norm(b));
      if (ia === -1) ia = 999; if (ib === -1) ib = 999;
      return ia - ib || a.localeCompare(b);
    });
    var current = this.filters.brand;
    box.innerHTML = ['all'].concat(brands).map(function (b) {
      var label = b === 'all' ? 'All' : b;
      var n = b === 'all' ? total : counts[b];
      return '<button type="button" class="rcl-pill" data-action="brand" data-value="' + esc(b) + '" aria-pressed="' + (current === b) + '">' +
        esc(label) + '<span class="rcl-pill__count" aria-label="' + n + ' rackets">' + n + '</span></button>';
    }).join('');
    this.updateBrandPicks();
  };

  /** Dot on brand pills that have a racket in the comparison. */
  RacketLab.prototype.updateBrandPicks = function () {
    var self = this;
    var picked = this.slots.slice(0, this.visible).map(function (h) { return h && self.byHandle[h] && self.byHandle[h].brand; });
    this.el.querySelectorAll('.rcl-pill[data-value]').forEach(function (p) {
      p.classList.toggle('has-pick', picked.indexOf(p.getAttribute('data-value')) > -1);
    });
  };

  RacketLab.prototype.filtered = function () {
    var f = this.filters, q = norm(f.q).trim();
    var terms = q ? q.split(/\s+/) : [];
    var list = this.items.filter(function (r) {
      if (f.brand !== 'all' && r.brand !== f.brand) return false;
      if (f.shape !== 'all' && r.shape !== f.shape) return false;
      if (f.level !== 'all' && r.levels.indexOf(f.level) === -1) return false;
      if (f.style !== 'all' && r.styles.indexOf(f.style) === -1) return false;
      if (terms.length) {
        var hay = norm(r.brand + ' ' + r.title + ' ' + r.name);
        for (var i = 0; i < terms.length; i++) if (hay.indexOf(terms[i]) === -1) return false;
      }
      return true;
    });
    var by = {
      featured: function (a, b) { return a.index - b.index; },
      'price-asc': function (a, b) { return a.price - b.price || a.index - b.index; },
      'price-desc': function (a, b) { return b.price - a.price || a.index - b.index; },
      'name-asc': function (a, b) { return (a.brand + ' ' + a.name).localeCompare(b.brand + ' ' + b.name); }
    }[f.sort] || function (a, b) { return a.index - b.index; };
    return list.sort(by);
  };

  RacketLab.prototype.renderGrid = function (append) {
    var list = this.filtered();
    var shown = list.slice(0, this.limit);
    var prevCount = append ? this.gridEl.children.length : 0;

    if (append) {
      this.gridEl.insertAdjacentHTML('beforeend', shown.slice(prevCount).map(this.tileHtml, this).join(''));
      var next = this.gridEl.children[prevCount];
      var btn = next && next.querySelector('button');
      if (btn) btn.focus({ preventScroll: true });
    } else {
      this.gridEl.innerHTML = shown.map(this.tileHtml, this).join('');
    }

    var count = this.$('[data-rcl-count]');
    if (count) count.textContent = list.length === this.items.length
      ? list.length + ' rackets'
      : list.length + ' of ' + this.items.length + ' rackets';

    // mobile: active-filter badge + "Show N rackets" in the sheet
    var f = this.filters, active = ['shape', 'level', 'style'].filter(function (k) { return f[k] !== 'all'; }).length;
    var badge = this.$('[data-rcl-filter-count]');
    if (badge) { badge.hidden = !active; badge.textContent = active; }
    var cta = this.$('[data-rcl-sheet-cta]');
    if (cta) cta.textContent = list.length ? 'Show ' + list.length + ' racket' + (list.length === 1 ? '' : 's') : 'No matches';

    var empty = this.$('[data-rcl-empty]');
    if (empty) empty.hidden = list.length > 0;

    var more = this.$('[data-action="more"]');
    if (more) {
      more.hidden = shown.length >= list.length;
      more.textContent = 'Show more rackets (' + (list.length - shown.length) + ')';
    }
    this.updateTiles();
  };

  RacketLab.prototype.tileHtml = function (r) {
    var badges = [];
    if (r.shape) badges.push(LABELS[r.shape]);
    if (r.styles.length) badges.push(LABELS[r.styles[0]]);
    var onSale = r.cmp > r.price;
    var label = r.brand + ' ' + r.name + ', ' + r.priceF.replace(/<[^>]*>/g, '') + (r.available ? '' : ', sold out') + '. Add to comparison';
    return '<li class="rcl-tile' + (r.available ? '' : ' is-soldout') + '" data-handle="' + esc(r.handle) + '">' +
      '<button type="button" class="rcl-tile__btn" data-action="pick" data-handle="' + esc(r.handle) + '" aria-label="' + esc(label) + '">' +
        '<span class="rcl-tile__media">' +
          (r.img ? '<img src="' + imgAt(r.img, 300) + '" srcset="' + srcset(r.img, [200, 300, 400, 600]) + '" sizes="(min-width: 1200px) 220px, (min-width: 750px) 25vw, 45vw" alt="" width="300" height="300" loading="lazy" decoding="async">' : '<span class="rcl-noimg">' + esc(r.brand.charAt(0)) + '</span>') +
          (r.available ? '' : '<span class="rcl-badge rcl-badge--soldout">Sold out</span>') +
          '<span class="rcl-badge rcl-badge--picked" data-rcl-picked hidden></span>' +
        '</span>' +
        '<span class="rcl-tile__brand">' + esc(r.brand) + '</span>' +
        '<span class="rcl-tile__name">' + esc(r.name) + '</span>' +
        (badges.length ? '<span class="rcl-tile__chips">' + badges.map(function (b) { return '<span class="rcl-chip">' + b + '</span>'; }).join('') + '</span>' : '') +
        '<span class="rcl-tile__foot">' +
          '<span class="rcl-tile__price">' + (onSale ? '<span class="rcl-price--sale">' + r.priceF + '</span> <s class="rcl-price--was">' + r.cmpF + '</s>' : r.priceF) + '</span>' +
          '<span class="rcl-tile__cta" aria-hidden="true">Compare</span>' +
        '</span>' +
      '</button>' +
    '</li>';
  };

  /** Mark tiles already loaded in a slot ("In slot 2") without re-rendering the grid. */
  RacketLab.prototype.updateTiles = function () {
    var self = this;
    this.gridEl.querySelectorAll('.rcl-tile').forEach(function (li) {
      var h = li.getAttribute('data-handle');
      var idx = self.slots.slice(0, self.visible).indexOf(h);
      var badge = li.querySelector('[data-rcl-picked]');
      var btn = li.querySelector('.rcl-tile__btn');
      var cta = li.querySelector('.rcl-tile__cta');
      li.classList.toggle('is-picked', idx > -1);
      badge.hidden = idx === -1;
      badge.textContent = idx > -1 ? 'In slot ' + (idx + 1) : '';
      if (cta) cta.textContent = idx > -1 ? 'Comparing' : 'Compare';
      btn.setAttribute('aria-pressed', String(idx > -1));
    });
  };

  /* ---------- mini tray (visible when the slots scroll out of view) ---------- */

  RacketLab.prototype.setupTray = function () {
    var self = this;
    this.tray = this.$('[data-rcl-tray]');
    var target = this.$('[data-rcl-compare]');
    if (!this.tray || !target || !('IntersectionObserver' in global)) return;
    this.slotsOutOfView = false;
    // Show the tray only once the shopper has scrolled past the slots (they sit above the viewport).
    new IntersectionObserver(function (entries) {
      var e = entries[0];
      self.slotsOutOfView = !e.isIntersecting && e.boundingClientRect.top < 0;
      self.updateTray();
    }, { rootMargin: '-120px 0px 0px 0px' }).observe(this.slotsEl);
    this.updateTray();
  };

  /** Brief "Added to slot 2" message + pulse on the tray (visible while the slots are scrolled away). */
  RacketLab.prototype.trayFeedback = function (msg) {
    var t = this.tray, self = this;
    if (!t || t.hidden) return;
    var count = t.querySelector('[data-rcl-tray-count]');
    t.classList.remove('is-bump'); void t.offsetWidth; t.classList.add('is-bump');
    if (count) count.textContent = msg;
    clearTimeout(this._trayT);
    this._trayT = setTimeout(function () { t.classList.remove('is-bump'); self.updateTray(); }, 1600);
  };

  RacketLab.prototype.updateTray = function () {
    if (!this.tray) return;
    var self = this;
    var picks = this.slots.slice(0, this.visible);
    var n = picks.filter(Boolean).length;
    var show = this.slotsOutOfView && n > 0;
    this.tray.hidden = !show;
    if (!show) return;
    this.tray.querySelector('[data-rcl-tray-thumbs]').innerHTML = picks.map(function (h, i) {
      var r = h && self.byHandle[h];
      return r && r.img
        ? '<span class="rcl-tray__thumb"><img src="' + imgAt(r.img, 96) + '" alt="' + esc(r.name) + '" width="40" height="40"></span>'
        : '<span class="rcl-tray__thumb is-empty" aria-label="Slot ' + (i + 1) + ' empty"></span>';
    }).join('');
    this.tray.querySelector('[data-rcl-tray-count]').textContent = n + ' of ' + this.visible + ' picked';
  };

  /* ---------- Ajax cart ---------- */

  /** Section ids the theme wants re-rendered after an add (Futurer xo-cart, Dawn drawer/notification). */
  RacketLab.prototype.cartSectionIds = function () {
    var ids = [];
    document.querySelectorAll('xo-cart-mini[id], xo-cart[id]').forEach(function (n) { ids.push(n.id); });
    var dawn = document.querySelector('cart-drawer, cart-notification');
    if (dawn && typeof dawn.getSectionsToRender === 'function') {
      try { dawn.getSectionsToRender().forEach(function (s) { ids.push(s.id); }); } catch (e) {}
    }
    return ids.filter(function (id, i) { return id && ids.indexOf(id) === i; }).slice(0, 5);
  };

  RacketLab.prototype.addToCart = function (slotIdx) {
    var self = this, r = this.byHandle[this.slots[slotIdx]];
    if (!r) return;
    var v = this.currentVariant(r);
    if (!v || !v.a || this.cartState[r.handle]) return;     // sold out or already busy

    var setState = function (state) {
      if (state) self.cartState[r.handle] = state; else delete self.cartState[r.handle];
      self.renderSlots();
    };
    delete this.cartMsg[r.handle];
    setState({ status: 'loading' });

    var body = { items: [{ id: v.id, quantity: 1 }] };
    var sections = this.cartSectionIds();
    if (sections.length) { body.sections = sections; body.sections_url = location.pathname; }

    // NOTE: the Futurer theme wraps window.fetch and shows its own cart toast
    // for any request to /cart/add – so we use fetch (not XHR) on purpose.
    fetch(root() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body)
    })
      .then(function (res) { return res.json().then(function (data) { return { ok: res.ok, data: data }; }); })
      .then(function (out) {
        if (!out.ok || out.data.status) throw new Error(out.data.description || out.data.message || 'Could not add to cart.');
        setState({ status: 'added' });
        self.announce(r.name + ' added to cart.');
        setTimeout(function () { setState(null); }, 2200);
        return self.refreshThemeCart(out.data, r);
      })
      .catch(function (err) {
        self.cartMsg[r.handle] = err.message;
        setState(null);
        self.announce('Could not add ' + r.name + ' to cart. ' + err.message);
      });
  };

  /** Tell the theme the cart changed: count bubble, drawer, notification. */
  RacketLab.prototype.refreshThemeCart = function (data, r) {
    var item = (data.items && data.items[0]) || data;
    var handled = false;

    return fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        // Futurer (xo-* components): update its store so <xo-cart-size> and the mini cart refresh
        if (global.xoStore && typeof global.xoStore.set === 'function') {
          try {
            var done = global.xoStore.set('xo-cart', function (s) {
              s = s || {};
              var merged = {};
              Object.keys(s.sections || {}).forEach(function (k) { merged[k] = s.sections[k]; });
              Object.keys(data.sections || {}).forEach(function (k) { if (data.sections[k]) merged[k] = data.sections[k]; });
              return Object.assign({}, s, {
                size: cart.item_count, sections: merged, item: item, isAdded: true,
                addIdLoading: '', variantId: item.variant_id,
                productIdsForCartNotification: [String(item.product_id)]
              });
            });
            if (typeof done === 'function') done('racket-lab/addCartSuccess');
            handled = true;
          } catch (e) { console.warn('[Racket Lab] xoStore update failed', e); }
        }

        // Dawn-style <cart-drawer> / <cart-notification>
        var dawn = document.querySelector('cart-drawer, cart-notification');
        if (!handled && dawn && typeof dawn.renderContents === 'function' && data.sections) {
          try {
            var state = Object.assign({}, item, { sections: data.sections });
            if (dawn.classList.contains('is-empty')) dawn.classList.remove('is-empty');
            dawn.renderContents(state);
            handled = true;
          } catch (e) { console.warn('[Racket Lab] cart drawer update failed', e); }
        }

        // Generic events many themes listen to
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: cart } }));
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: item } }));

        // Plain count bubbles
        document.querySelectorAll('[data-cart-count], .cart-count-bubble span[aria-hidden="true"]').forEach(function (n) {
          n.textContent = cart.item_count;
        });

        // Our own confirmation only when the theme has none (Futurer shows window.showCartToast itself)
        if (!handled && typeof global.showCartToast !== 'function') showOwnToast(r);
      })
      .catch(function () {
        if (typeof global.showCartToast !== 'function') showOwnToast(r);
      });
  };

  function showOwnToast(r) {
    var t = document.createElement('div');
    t.className = 'rcl-toast';
    t.setAttribute('role', 'status');
    t.innerHTML = '<span>' + esc(r.brand + ' ' + r.name) + ' added to cart</span><a href="' + root() + 'cart">View cart</a>';
    document.body.appendChild(t);
    requestAnimationFrame(function () { t.classList.add('is-in'); });
    setTimeout(function () { t.classList.remove('is-in'); setTimeout(function () { t.remove(); }, 300); }, 4000);
  }

  /* ---------- boot ---------- */

  function init(scope) {
    (scope || document).querySelectorAll('[data-racket-lab]').forEach(function (el) {
      if (!el.__racketLab) el.__racketLab = new RacketLab(el);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();
  // Theme editor: re-init when the section is added/re-rendered
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });

  global.RacketLab = RacketLab;
})(typeof window !== 'undefined' ? window : globalThis);
